// อัปโหลดแผนวิ่งของสาขา — mirror ของ DTO ฝั่ง backend
// (src/modules/pathwayUpload/dto/response.ts)
//
// การอัปโหลดเป็น 2 ขั้น: preview คืน diff + upload_id โดยยังไม่เขียน DB,
// แล้ว confirm ถึงจะเขียนจริงจากแผนที่ค้างไว้ (ไม่ได้อ่านไฟล์ซ้ำ)

/** 1 = จันทร์ … 7 = อาทิตย์ (ตรงกับ pathway_day ใน DB) */
export type PathwayDay = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const PATHWAY_DAY_LABEL: Record<PathwayDay, string> = {
  1: "จันทร์",
  2: "อังคาร",
  3: "พุธ",
  4: "พฤหัสบดี",
  5: "ศุกร์",
  6: "เสาร์",
  7: "อาทิตย์",
};

export type PathwayStopChange = "UNCHANGED" | "ADDED" | "REMOVED" | "MOVED";

/** รหัสเหตุผลที่ backend ใช้ (PathwaySkipReasonEnum / PathwayWarningReasonEnum) */
export type PathwayUploadReasonCode =
  | "NOT_A_CUSTOMER_CODE"
  | "MISSING_CUSTOMER_CODE"
  | "DUPLICATE_CUSTOMER"
  | "MISSING_STOP_NO";

/**
 * ปกติ backend แปลง `reason` เป็นภาษาไทยมาให้แล้ว แต่ถ้า backend ยังไม่มีคำแปล
 * ของรหัสไหน มันจะส่งรหัสดิบมา — ตารางนี้กันไว้ไม่ให้ผู้ใช้เห็นรหัสภาษาอังกฤษ
 * ส่วนค่าที่ไม่รู้จัก (เหตุผลใหม่ในอนาคต) แสดงตามที่ได้มา
 */
export const PATHWAY_UPLOAD_REASON_LABEL: Record<PathwayUploadReasonCode, string> = {
  NOT_A_CUSTOMER_CODE: "ไม่ใช่รหัสร้าน",
  MISSING_CUSTOMER_CODE: "มีชื่อร้านแต่ไม่มีรหัสร้าน",
  DUPLICATE_CUSTOMER: "ร้านซ้ำในวันเดียวกัน (เก็บลำดับแรกไว้)",
  MISSING_STOP_NO: "ไม่มีลำดับในไฟล์ — ระบบต่อท้ายให้ตามตำแหน่งในไฟล์",
};

export const pathwayReasonLabel = (reason: string): string =>
  PATHWAY_UPLOAD_REASON_LABEL[reason as PathwayUploadReasonCode] ?? reason;

/** แถวในไฟล์ที่ถูกข้าม หรือที่เข้าระบบแต่ควรดูซ้ำ */
export interface PathwayUploadReportRow {
  /** `ผ.4!L33` — ชีต + เซลล์ ให้ GM กด Ctrl+G ไปแก้ได้ตรงจุด */
  location: string;
  route_code: string | null;
  pathway_day: PathwayDay | null;
  cell_text: string;
  reason: string;
}

export interface PathwayUploadDiffRow {
  stop_no: number | null;
  previous_stop_no: number | null;
  customer_code: string;
  /** ชื่อร้านจากฐานข้อมูล — ไม่ใช่ชื่อในไฟล์ */
  customer_name: string | null;
  change: PathwayStopChange;
  /** ชื่อในไฟล์ไม่ตรงกับในระบบ = สัญญาณว่าพิมพ์รหัสผิดไปชนร้านอื่น */
  name_mismatch: boolean;
  excel_name?: string;
}

export interface PathwayUploadDaySummary {
  route_code: string;
  route_uuid: string;
  pathway_day: PathwayDay;
  current_count: number;
  new_count: number;
  added: number;
  removed: number;
  moved: number;
  skipped: number;
  stops: PathwayUploadDiffRow[];
}

export interface PathwayUploadPreviewSummary {
  plan_sheets: number;
  routes: number;
  rows_read: number;
  rows_to_apply: number;
  rows_skipped: number;
  rows_warned: number;
  /** ไฟล์นี้แก้แผนของวันนี้ — ยืนยันแล้วพนักงานขายเห็นทันที */
  affects_today: boolean;
  today_pathway_day: PathwayDay;
}

export interface PathwayUploadPreviewData {
  upload_id: string;
  expires_in_seconds: number;
  file_name: string;
  branch_uuid: string;
  summary: PathwayUploadPreviewSummary;
  days: PathwayUploadDaySummary[];
  skipped: PathwayUploadReportRow[];
  /** แถวที่จะถูกเขียน แต่ต้องให้ GM เห็น (เช่น ไม่มีลำดับในไฟล์) */
  warnings: PathwayUploadReportRow[];
}

export interface PathwayUploadConfirmData {
  upload_id: string;
  branch_uuid: string;
  routes_applied: number;
  days_applied: number;
  rows_deleted: number;
  rows_inserted: number;
}

export interface PathwayUploadPreviewResponse {
  statusCode: number;
  title: string;
  message: string;
  data: PathwayUploadPreviewData;
  error: object | null;
}

export interface PathwayUploadConfirmResponse {
  statusCode: number;
  title: string;
  message: string;
  data: PathwayUploadConfirmData;
  error: object | null;
}
